import { type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import { CURRENCIES, type Currency } from '../model';

interface AmountBoxProps {
  icon: ReactNode;
  value: number | null;
  step: number;
  currency: Currency;
  readOnly: boolean;
  onValueChange: (value: number | null) => void;
  onCurrencyChange: (currency: Currency) => void;
}

/**
 * Cost amount box mirroring the design's affix control: a leading pax/group icon, a right-aligned
 * numeric input with the native spinner suppressed (`.tp-nospin`), and a trailing currency picker.
 * Self-styled with global `phx-*` tokens so it renders correctly inside the body-portalled drawer.
 */
export function AmountBox({
  icon,
  value,
  step,
  currency,
  readOnly,
  onValueChange,
  onCurrencyChange,
}: AmountBoxProps) {
  return (
    <div
      className={cn(
        'phx-affix h-phx-control rounded-phx bg-phx-surface border-phx-border-2 flex items-center border pr-1.5',
        'transition-[border-color,box-shadow,background-color]',
        'hover:border-phx-border-3 focus-within:border-phx-primary focus-within:shadow-phx-focus',
        readOnly && 'bg-phx-surface-3 pointer-events-none'
      )}
    >
      <span className="text-phx-text-muted flex flex-none items-center pl-1.75">{icon}</span>
      <input
        className="tp-nospin text-phx-md text-phx-text min-w-0 flex-1 border-0 bg-transparent px-1.75 text-right outline-none"
        type="number"
        min="0"
        step={step}
        inputMode="decimal"
        placeholder="0"
        value={value ?? ''}
        disabled={readOnly}
        onChange={(event) => {
          const raw = event.target.value;
          onValueChange(raw === '' ? null : Number(raw));
        }}
      />
      <select
        className="text-phx-text-muted text-phx-xs flex-none cursor-pointer appearance-none border-0 bg-transparent pl-1 font-mono outline-none disabled:cursor-default"
        value={currency}
        disabled={readOnly}
        onChange={(event) => onCurrencyChange(event.target.value as Currency)}
      >
        {CURRENCIES.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
    </div>
  );
}
